import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Avatar, Backdrop, Box, Button, Fade, IconButton, Stack, styled, SvgIcon, Typography, useTheme, Zoom, } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useLocation } from 'react-router-dom';
import { ChevronDownIcon, PlusIcon, Bars3Icon, XMarkIcon, } from '@heroicons/react/24/solid';
import { useSidebar } from './SidebarContext';
import { SIDE_NAV_WIDTH } from './constants';
const ExplanationButtons = styled(Button)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(9),
    color: theme.palette.secondary.dark,
    borderColor: theme.palette.secondary.dark,
    '&:hover': {
        borderColor: theme.palette.secondary.dark,
    },
}));
export function Topbar({ pageDescription, explanationVideo, resetPageData, userInitial, renderAccountPopover, rightSlot, }) {
    const theme = useTheme();
    const [accordionExpanded, setAccordionExpanded] = useState(false);
    const [videoOverlayOpen, setVideoOverlayOpen] = useState(false);
    const [accountPopoverOpen, setAccountPopoverOpen] = useState(false);
    const accountAnchorRef = useRef(null);
    const { pathname } = useLocation();
    const { toggleMobileSidebar } = useSidebar();
    const transitionDuration = {
        enter: theme.transitions.duration.enteringScreen,
        exit: theme.transitions.duration.leavingScreen,
    };
    const toggleAccordion = () => setAccordionExpanded((prev) => !prev);
    useEffect(() => {
        setAccordionExpanded(false);
        resetPageData?.();
    }, [pathname, resetPageData]);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { component: 'header', sx: {
                    position: 'fixed',
                    zIndex: (t) => t.zIndex.appBar,
                    left: {
                        xs: 0,
                        lg: `${SIDE_NAV_WIDTH}px`,
                    },
                    top: 0,
                    width: {
                        xs: '100%',
                        lg: `calc(100% - ${SIDE_NAV_WIDTH}px)`,
                    },
                }, children: [_jsxs(Accordion, { square: true, sx: {
                            backdropFilter: 'blur(4px)',
                            backgroundColor: (t) => alpha(t.palette.background.default, 0.8),
                            transition: theme.transitions.create([
                                'background-color',
                                'backdrop-filter',
                                'box-shadow',
                            ]),
                            borderTopRightRadius: 0,
                            borderTopLeftRadius: 0,
                            borderBottomRightRadius: 8,
                            borderBottomLeftRadius: 8,
                            ...(accordionExpanded && {
                                backdropFilter: 'blur(0px)',
                                backgroundColor: (t) => alpha(t.palette.background.default, 1),
                                boxShadow: theme.shadows[3],
                            }),
                        }, expanded: accordionExpanded, children: [_jsxs(AccordionSummary, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    p: 3,
                                    borderRadius: 0,
                                    '& .MuiAccordionSummary-content': { my: 0 },
                                }, children: [_jsxs(Stack, { sx: { alignItems: 'center' }, spacing: 3, direction: 'row', children: [_jsx(IconButton, { onClick: toggleMobileSidebar, sx: { display: { lg: 'none' } }, children: _jsx(SvgIcon, { children: _jsx(Bars3Icon, {}) }) }), _jsx(Zoom, { in: !!pageDescription, timeout: transitionDuration, unmountOnExit: true, children: _jsxs(ExplanationButtons, { variant: 'outlined', color: 'secondary', onClick: toggleAccordion, children: [_jsx(SvgIcon, { sx: {
                                                                transition: (t) => t.transitions.create([
                                                                    'transform',
                                                                ]),
                                                                transform: `rotateZ(${accordionExpanded ? 180 : 0}deg)`,
                                                            }, fontSize: 'small', children: _jsx(ChevronDownIcon, {}) }), "\u00A0 Infos zur Seite"] }) }), _jsx(Zoom, { in: !!explanationVideo, timeout: transitionDuration, style: {
                                                    transitionDelay: explanationVideo
                                                        ? '80ms'
                                                        : '0ms',
                                                }, unmountOnExit: true, children: _jsxs(ExplanationButtons, { variant: 'outlined', onClick: () => setVideoOverlayOpen(true), children: [_jsx(SvgIcon, { fontSize: 'small', children: _jsx(PlusIcon, {}) }), "\u00A0 Erkl\u00E4rvideo"] }) })] }), rightSlot ?? (_jsx(Avatar, { color: 'secondary', onClick: () => setAccountPopoverOpen(true), ref: accountAnchorRef, sx: (t) => ({
                                            ml: 'auto',
                                            backgroundColor: t.palette.primary.main,
                                            cursor: 'pointer',
                                            height: 48,
                                            width: 48,
                                            fontSize: '1.25rem',
                                        }), children: userInitial?.toUpperCase() ?? '' }))] }), _jsx(AccordionDetails, { sx: { px: 10, pb: 6 }, children: _jsx(Fade, { style: {
                                        transitionDelay: accordionExpanded
                                            ? '80ms'
                                            : '0ms',
                                    }, in: accordionExpanded, children: _jsxs("div", { children: [_jsx(Typography, { variant: 'h4', sx: { mb: 6 }, children: pageDescription?.title }), pageDescription?.body] }) }) })] }), renderAccountPopover?.({
                        anchorEl: accountAnchorRef.current,
                        open: accountPopoverOpen,
                        onClose: () => setAccountPopoverOpen(false),
                    })] }), _jsx(Backdrop, { onClick: toggleAccordion, sx: { zIndex: (t) => t.zIndex.appBar - 1 }, open: accordionExpanded }), explanationVideo && (_jsxs(Backdrop, { sx: {
                    color: '#fff',
                    zIndex: (t) => t.zIndex.drawer + 1,
                }, open: videoOverlayOpen, onClick: () => setVideoOverlayOpen(false), children: [_jsx(IconButton, { sx: (t) => ({
                            position: 'absolute',
                            top: t.spacing(4),
                            right: t.spacing(4),
                            color: '#fff',
                        }), onClick: (e) => {
                            e.stopPropagation();
                            setVideoOverlayOpen(false);
                        }, children: _jsx(SvgIcon, { children: _jsx(XMarkIcon, {}) }) }), _jsx(Box, { component: 'iframe', src: explanationVideo, title: 'Erkl\u00E4rvideo', sx: {
                            width: '80%',
                            minWidth: 300,
                            height: 360,
                            border: 0,
                        }, onClick: (e) => e.stopPropagation() })] }))] }));
}
